import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Building2, ArrowRight, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const HERO =
  "https://images.unsplash.com/photo-1542744173-8e7e53415bb0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwyfHxjb3Jwb3JhdGUlMjBzYWxlcyUyMG1lZXRpbmclMjB0ZWFtfGVufDB8fHx8MTc4OTc1NTg2MHww&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success(`Welcome, ${u.name}`);
      navigate("/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left brand panel */}
      <div className="relative hidden lg:flex flex-col justify-between p-12 bg-[#0F172A] overflow-hidden">
        <img
          src={HERO}
          alt="Sales team"
          className="absolute inset-0 w-full h-full object-cover opacity-20"
        />
        <div className="relative z-10 flex items-center gap-3 text-white">
          <div className="h-11 w-11 rounded-xl bg-[#1E3A8A] flex items-center justify-center">
            <Building2 className="h-6 w-6" />
          </div>
          <div>
            <p className="font-heading font-bold text-lg leading-none">Tamanna Sales Hub</p>
            <p className="text-xs text-slate-400 mt-1">Lead Management Portal</p>
          </div>
        </div>
        <div className="relative z-10 text-white max-w-md">
          <h1 className="font-heading text-4xl xl:text-5xl font-bold tracking-tight leading-tight">
            Every enquiry.<br />Every executive.<br />One pipeline.
          </h1>
          <p className="text-slate-300 mt-5 text-sm leading-relaxed">
            Track banquet & convention leads across all Tamanna properties with
            role-based access, live status workflows and instant reporting.
          </p>
        </div>
        <div className="relative z-10 flex items-center gap-2 text-slate-400 text-xs">
          <ShieldCheck className="h-4 w-4" />
          Strict role-based access control
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 sm:p-12 bg-[#F8FAFC]">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <div className="h-10 w-10 rounded-xl bg-[#1E3A8A] flex items-center justify-center text-white">
              <Building2 className="h-5 w-5" />
            </div>
            <p className="font-heading font-bold text-lg">Tamanna Sales Hub</p>
          </div>

          <h2 className="font-heading text-2xl font-bold text-slate-900">Sign in</h2>
          <p className="text-sm text-slate-500 mt-1">Access your sales lead dashboard</p>

          <form onSubmit={submit} className="mt-8 space-y-5" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                data-testid="login-email-input"
                placeholder="you@tamannahotels.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="bg-white h-11"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                data-testid="login-password-input"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="bg-white h-11"
              />
            </div>

            {error && (
              <p data-testid="login-error" className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">
                {error}
              </p>
            )}

            <Button
              type="submit"
              data-testid="login-submit-button"
              disabled={loading}
              className="w-full h-11 bg-[#1E3A8A] hover:bg-[#172554] text-white font-medium group"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  Sign in
                  <ArrowRight className="h-4 w-4 ml-1 transition-transform group-hover:translate-x-0.5" />
                </>
              )}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
